/**
 * LeaseWizardPage
 * 9-step lease creation wizard at /units/:unitId/create-lease
 *
 * Step 8 (Review, Preview & Edit) merges the former Step 8 + Step 9.
 * Step 9 (Generate & Send) is the former Step 10.
 */

import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useLeaseWizard } from './useLeaseWizard';
import { Step1JurisdictionInfo } from './steps/Step1JurisdictionInfo';
import { Step2LandlordContact } from './steps/Step2LandlordContact';
import { Step3Tenants } from './steps/Step3Tenants';
import { Step4LeaseTerms } from './steps/Step4LeaseTerms';
import { Step5FinancialTerms } from './steps/Step5FinancialTerms';
import { Step6PoliciesRules } from './steps/Step6PoliciesRules';
import { Step7PetDetails } from './steps/Step7PetDetails';
import { Step8ReviewPreview } from './steps/Step8ReviewPreview';
import { Step10GenerateSend } from './steps/Step10GenerateSend';
import { getUnit, getManagerProfile, checkDisclosureReadiness, type UnitWithRelations, type DisclosureReadinessResponse } from '../../lib/api';
import { bindMoveInLink } from '../../lib/move-in.api';
import { IncorporationBanner } from '../../components/move-in/IncorporationBanner';
import { PropertyTimezoneProvider } from '../../contexts/PropertyTimezoneContext';
import { useUnitTimezone } from '../../hooks/useUnitTimezone';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import type { MoveInProjection } from '../../types/move-in';

const STEP_LABELS = [
  'Jurisdiction',
  'Contact',
  'Tenants',
  'Lease Terms',
  'Financial',
  'Policies',
  'Pet Details',
  'Review & Edit',
  'Generate',
];

export function LeaseWizardPage() {
  const { unitId } = useParams<{ unitId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [unit, setUnit] = useState<UnitWithRelations | null>(null);
  const [unitLoading, setUnitLoading] = useState(true);
  // Sourced by unit ID via the batched cache (issue #910) — not from unit.state,
  // which requires whoever built the payload to have remembered to select it.
  const timezoneQuery = useUnitTimezone(unitId);
  const propertyTimezone = timezoneQuery.data ?? 'America/New_York';
  const [hasUnitConflict, setHasUnitConflict] = useState(false);
  const [disclosureReadiness, setDisclosureReadiness] = useState<DisclosureReadinessResponse | null>(null);
  const [missingRequiredMetadata, setMissingRequiredMetadata] = useState(0);
  // Highest step the user has reached — lets them jump back to any visited step.
  const [maxStep, setMaxStep] = useState<number>(1);
  // Sync maxStep upward whenever currentStep advances (covers draft load too).
  const prevCurrentStep = useRef(1);

  const existingLeaseId = searchParams.get('leaseId') ?? undefined;
  const stepParam = searchParams.get('step');
  const wizard = useLeaseWizard(unitId!, existingLeaseId);
  const { state } = wizard;

  // Banner state — dismissed locally for this wizard session only
  const [bannerDismissed, setBannerDismissed] = useState(false);

  // Move-in projection (suggestions auto-applied to the wizard; link back-filled on first load)
  const [moveInProjection, setMoveInProjection] = useState<MoveInProjection | null>(null);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const prefillApplied = useRef(false);

  // Load move-in projection on mount (non-fatal — banner silently hides on failure)
  useEffect(() => {
    if (!unitId) return;
    import('../../lib/move-in.api').then(({ getMoveIn }) =>
      getMoveIn(unitId)
        .then((proj) => setMoveInProjection(proj))
        .catch(() => {/* Non-fatal */})
    );
  }, [unitId]);

  // Auto-apply pre-fill once, after BOTH the saved draft has loaded and the suggestions are
  // available. applyPreFill is non-destructive (skips dirty + already-populated fields), so it
  // never clobbers draft data — no landlord action required. Also back-fills the MoveInLink
  // (BR-MI-02) so the unit↔lead binding survives lease creation started from the wizard.
  useEffect(() => {
    if (prefillApplied.current) return;
    if (!draftLoaded || !moveInProjection) return;
    prefillApplied.current = true;
    const active = moveInProjection.suggestions.filter((s) => s.preference === null);
    if (active.length > 0) {
      wizard.applyPreFill(active);
      if (unitId && moveInProjection.link?.leadId) {
        bindMoveInLink(unitId, moveInProjection.link.leadId).catch(() => {
          console.warn('MoveInLink back-fill failed — proceeding without binding');
        });
      }
    }
  }, [draftLoaded, moveInProjection]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!unitId) return;
    getUnit(unitId)
      .then(r => {
        setUnit(r.unit);
        setMissingRequiredMetadata(r.unit.metadataSchema?.summary.missingRequired ?? 0);
      })
      .catch(() => setUnit(null))
      .finally(() => setUnitLoading(false));

    // Auto-populate landlord contact from the manager's Settings → Profile record —
    // NOT the auth session, whose displayName is a one-time snapshot from registration
    // and carries no mailing address at all.
    getManagerProfile()
      .then(profile => {
        const fullName = [profile.firstName, profile.lastName].filter(Boolean).join(' ') || profile.email || '';
        wizard.initLandlordContact(fullName, profile.email ?? '', {
          mailingAddress: [profile.address1, profile.address2].filter(Boolean).join(', '),
          city: profile.city ?? '',
          state: profile.state ?? '',
          zipCode: profile.zipCode ?? '',
          phone: profile.phone ?? '',
        });
      })
      .catch(() => {/* Non-fatal — landlord contact fields stay blank/overridable */});

    checkDisclosureReadiness(unitId)
      .then(res => setDisclosureReadiness(res))
      .catch(() => setDisclosureReadiness(null));

    // Load existing draft first, then fall back to property defaults
    wizard.loadDraft().then(() => {
      wizard.loadDefaults();
      // If a specific step was requested (e.g., re-edit flow), jump there after draft loads
      if (stepParam) {
        const n = parseInt(stepParam, 10);
        // Draft migration guard: old step 9 (Edit) → new step 8 (Review & Edit)
        // old step 10 (Generate) → new step 9 (Generate)
        const mappedN = n === 10 ? 9 : n === 9 ? 8 : n;
        if (mappedN >= 1 && mappedN <= 9) {
          wizard.goToStep(mappedN as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9);
        }
      }
      // Draft + defaults are now settled — safe to auto-apply move-in pre-fill into the gaps.
      setDraftLoaded(true);
    });
  }, [unitId]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentStep = state.currentStep;

  // Keep maxStep in sync whenever currentStep moves forward (manual nav or draft load).
  useEffect(() => {
    if (currentStep > prevCurrentStep.current) {
      setMaxStep(prev => Math.max(prev, currentStep));
    }
    prevCurrentStep.current = currentStep;
  }, [currentStep]);

  const handleNext = async () => {
    if (currentStep === 8) {
      // Validate anchors before advancing to step 9 (Generate & Send)
      const valid = await wizard.validateAnchors();
      if (!valid) return; // wizard.validateAnchors sets state.error
      wizard.goToStep(9);
      return;
    }
    if (currentStep < 9) {
      wizard.goToStep((currentStep + 1) as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      wizard.goToStep((currentStep - 1) as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9);
    } else {
      navigate(`/units/${unitId}`);
    }
  };

  const canGoNext = (): boolean => {
    switch (currentStep) {
      case 2: {
        // Step 2: Landlord Contact — require all required fields and at least one contact method
        const c = state.landlordContact;
        return Boolean(c.fullName) &&
          Boolean(c.mailingAddress) &&
          Boolean(c.city) &&
          Boolean(c.state) &&
          Boolean(c.zipCode) &&
          Boolean(c.email) &&
          c.contactMethods.length > 0;
      }
      case 3:
        return state.selectedTenantIds.length > 0;
      case 4:
        return Boolean(state.inputs.startDate) &&
          (state.inputs.leaseType === 'month-to-month' || Boolean(state.inputs.endDate)) &&
          !hasUnitConflict;
      case 5:
        return state.inputs.rentAmount != null &&
          state.inputs.securityDeposit != null &&
          state.inputs.paymentDueDay != null;
      case 6:
        return state.inputs.entryNoticeHours != null;
      case 8:
        // Anchor validation happens async in handleNext; allow Next synchronously
        return true;
      default:
        return true;
    }
  };

  const unitAddress = unit
    ? `${unit.address1}, ${unit.city}, ${unit.state}`
    : unitId ?? '';

  return (
    <div className="max-w-4xl mx-auto pb-28 md:pb-6" data-testid="lease-wizard-page">
      {/* Header */}
      <div className="px-4 md:px-6 lg:px-8 pt-4 md:pt-6 lg:pt-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
          <div>
            <Link
              to={`/units/${unitId}`}
              className="text-sm text-primary hover:text-primary/80 no-underline inline-block mb-1"
            >
              ← Back to Unit
            </Link>
            <h1 className="text-xl md:text-2xl font-semibold text-foreground m-0">
              {unitLoading ? 'Create Lease' : `Create Lease — ${unitAddress}`}
            </h1>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            {currentStep > 1 && state.jurisdictionInfo && (
              <Badge variant="info" data-testid="jurisdiction-badge">
                {state.jurisdictionInfo.displayName}
              </Badge>
            )}
            <Button
              type="button"
              variant="outline"
              data-testid="wizard-save-draft-button"
              onClick={wizard.saveDraft}
              disabled={state.isSaving}
              className="min-h-[44px]"
            >
              {state.isSaving ? 'Saving...' : 'Save Draft'}
            </Button>
          </div>
        </div>

        {/* Step indicator — compact dots on mobile, full labels on desktop */}
        <div className="mb-6">
          {/* Mobile: compact dot stepper */}
          <div className="flex items-center justify-between gap-1 md:hidden mb-2">
            <span className="text-xs font-medium text-muted-foreground">
              Step {currentStep}: {STEP_LABELS[currentStep - 1]}
            </span>
            <span className="text-xs text-muted-foreground">{currentStep}/9</span>
          </div>
          <div className="flex items-center gap-1 md:hidden">
            {STEP_LABELS.map((_, index) => {
              const stepNum = (index + 1) as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
              const isActive = stepNum === currentStep;
              const isReachable = stepNum <= maxStep && !isActive;
              return (
                <button
                  key={stepNum}
                  type="button"
                  data-testid={`wizard-progress-step-${stepNum}-mobile`}
                  onClick={() => { if (stepNum <= maxStep) wizard.goToStep(stepNum); }}
                  disabled={stepNum > maxStep}
                  aria-current={isActive ? 'step' : undefined}
                  aria-label={`Step ${stepNum}: ${STEP_LABELS[index]}`}
                  className={`flex-1 h-2 rounded-full transition-colors ${
                    isActive
                      ? 'bg-primary'
                      : isReachable
                      ? 'bg-primary/40 cursor-pointer'
                      : 'bg-border cursor-not-allowed'
                  }`}
                />
              );
            })}
          </div>

          {/* Desktop: full labeled stepper */}
          <div className="hidden md:flex items-center gap-1 overflow-x-auto pb-2">
            {STEP_LABELS.map((label, index) => {
              const stepNum = (index + 1) as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
              const isActive = stepNum === currentStep;
              const isReachable = stepNum <= maxStep && !isActive;
              return (
                <div key={stepNum} className="flex items-center min-w-0">
                  <button
                    type="button"
                    data-testid={`wizard-progress-step-${stepNum}`}
                    onClick={() => { if (stepNum <= maxStep) wizard.goToStep(stepNum); }}
                    disabled={stepNum > maxStep}
                    className={`flex flex-col items-center gap-1 px-2 py-1 rounded-lg transition-colors min-h-[44px] min-w-[60px] ${
                      isActive
                        ? 'bg-primary text-primary-foreground cursor-default'
                        : isReachable
                        ? 'bg-primary/10 text-primary hover:bg-primary/20 cursor-pointer'
                        : 'bg-muted text-muted-foreground cursor-not-allowed'
                    }`}
                    aria-current={isActive ? 'step' : undefined}
                  >
                    <span className={`text-sm font-bold ${isActive ? 'text-primary-foreground' : ''}`}>
                      {stepNum}
                    </span>
                    <span className="text-xs truncate max-w-[64px]">{label}</span>
                  </button>
                  {index < STEP_LABELS.length - 1 && (
                    <div className={`h-0.5 flex-1 mx-1 min-w-[8px] ${isReachable ? 'bg-primary/40' : 'bg-border'}`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Incorporation Banner — informational note of what was auto-pre-filled */}
      {unitId && !bannerDismissed && (
        <div className="mx-4 md:mx-6 lg:mx-8 mb-4">
          <PropertyTimezoneProvider timezone={propertyTimezone}>
            <IncorporationBanner
              unitId={unitId}
              onDismiss={() => setBannerDismissed(true)}
            />
          </PropertyTimezoneProvider>
        </div>
      )}

      {/* Step content */}
      <div className="bg-card rounded-lg border border-border p-4 md:p-6 mx-4 md:mx-6 lg:mx-8 min-h-[400px]">
        {currentStep === 1 && (
          <Step1JurisdictionInfo
            state={state}
            loadJurisdiction={wizard.loadJurisdiction}
            unitId={unitId}
            disclosureReadiness={disclosureReadiness}
            missingRequiredMetadata={missingRequiredMetadata}
          />
        )}
        {currentStep === 2 && (
          <Step2LandlordContact
            state={state}
            updateLandlordContact={wizard.updateLandlordContact}
          />
        )}
        {currentStep === 3 && (
          <Step3Tenants
            state={state}
            unitTenants={unit?.tenants ?? []}
            selectedTenantIds={state.selectedTenantIds}
            setSelectedTenantIds={wizard.setSelectedTenantIds}
            addCoSigner={wizard.addCoSigner}
            removeCoSigner={wizard.removeCoSigner}
          />
        )}
        {currentStep === 4 && (
          <Step4LeaseTerms
            state={state}
            updateInputs={wizard.updateInputs}
            unitId={unitId}
            onConflictChange={setHasUnitConflict}
            timezone={propertyTimezone}
          />
        )}
        {currentStep === 5 && (
          <Step5FinancialTerms
            state={state}
            updateInputs={wizard.updateInputs}
            updateParking={wizard.updateParking}
            updateUtilities={wizard.updateUtilities}
          />
        )}
        {currentStep === 6 && (
          <Step6PoliciesRules
            state={state}
            updateInputs={wizard.updateInputs}
            setShortTermRentalProhibited={wizard.setShortTermRentalProhibited}
            updateSmokingPolicy={wizard.updateSmokingPolicy}
            updateRentersInsurance={wizard.updateRentersInsurance}
          />
        )}
        {currentStep === 7 && (
          <Step7PetDetails
            state={state}
            updatePets={wizard.updatePets}
            addAnimal={wizard.addAnimal}
            updateAnimal={wizard.updateAnimal}
            removeAnimal={wizard.removeAnimal}
            addAssistanceAnimal={wizard.addAssistanceAnimal}
            updateAssistanceAnimal={wizard.updateAssistanceAnimal}
            removeAssistanceAnimal={wizard.removeAssistanceAnimal}
            jurisdictionState={
              state.jurisdictionInfo?.chain
                ?.find(c => c.startsWith('states/'))
                ?.split('/')[1] ?? ''
            }
          />
        )}
        {currentStep === 8 && (
          <Step8ReviewPreview
            state={state}
            unitTenants={unit?.tenants ?? []}
            previewLease={wizard.previewLease}
            goToStep={wizard.goToStep}
            setEditedContent={wizard.setEditedContent}
          />
        )}
        {currentStep === 9 && (
          <Step10GenerateSend
            state={state}
            generateLease={wizard.generateLease}
            sendForSigning={wizard.sendForSigning}
            saveDraft={wizard.saveDraft}
            disclosureReadiness={disclosureReadiness}
            unitId={unitId}
          />
        )}
      </div>

      {/* Desktop navigation buttons (inline) */}
      <div className="hidden md:flex justify-between items-center px-6 lg:px-8 mt-6">
        <Button
          type="button"
          variant="outline"
          data-testid="wizard-back-button"
          onClick={handleBack}
          className="min-h-[44px]"
        >
          {currentStep === 1 ? 'Cancel' : 'Back'}
        </Button>
        {currentStep < 9 && (
          <Button
            type="button"
            data-testid="wizard-next-button"
            onClick={handleNext}
            disabled={!canGoNext() || state.isLoading}
            className="min-h-[44px]"
          >
            Next
          </Button>
        )}
      </div>

      {/* Mobile: sticky bottom ActionBar */}
      <div className="md:hidden fixed bottom-[var(--bottom-tab-height)] left-0 right-0 z-40 bg-card border-t border-border px-4 py-3 shadow-[0_-2px_8px_rgba(0,0,0,0.08)]">
        <div className="flex justify-between items-center gap-3">
          <Button
            type="button"
            variant="outline"
            data-testid="wizard-back-button-mobile"
            onClick={handleBack}
            className="min-h-[44px] flex-1"
          >
            {currentStep === 1 ? 'Cancel' : 'Back'}
          </Button>
          {currentStep < 9 && (
            <Button
              type="button"
              data-testid="wizard-next-button-mobile"
              onClick={handleNext}
              disabled={!canGoNext() || state.isLoading}
              className="min-h-[44px] flex-1"
            >
              Next
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default LeaseWizardPage;
